import React, {useEffect, useRef, useState} from "react"
import {Button, Stack, Typography, TextField, CircularProgress} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {QRCodeSVG} from "qrcode.react";
import {ApiResponse} from "apisauce";
import api from "../../../Services/api";
import PrintReceipt from "../Common/PrintReceipt";
import BackButton from "../Common/BackButton";
import {ServiceCardType} from "../Types";
import {useTranslation} from "react-i18next";
import PaymentHeaders from './PaymentHeaders'
import {numberToCurrencyString} from "../../../Utils/helpers";

const { getCheckIn, createPayment, cancelPayment } = api.create();

interface PayOnlineProps {
    serviceCard: ServiceCardType;
    setServiceCard: (serviceCard: ServiceCardType) => void
    setPaymentMade: (paymentMade: boolean) => void;
    onBack: () => void;
}

const PayOnline = ({ serviceCard, setServiceCard, setPaymentMade, onBack }: PayOnlineProps) => {
    const { t } = useTranslation()

    const [payOnlineResponse, setPayOnlineResponse] = useState<any>(null)
    const [payOnlinePrice, setPayOnlinePrice] = useState<number>(null)
    const [paymentNotice, setPaymentNotice] = useState<any>(null)
    const [printReceipt, setPrintReceipt] = useState<boolean>(false)
    const originalEmail = serviceCard.sale?.customer?.email || ""
    const [email, setEmail] = useState(originalEmail)
    const [editingEmail, setEditingEmail] = useState(originalEmail?.length < 1)
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const [error, setError] = useState<string >();


    const initiatePayment = async () => {
        const fetchUpdatedServiceCard = async () => {
            const checkInResponse: ApiResponse<any> = await getCheckIn(serviceCard.token)
            if (checkInResponse.ok) {
                const updatedServiceCard = checkInResponse.data as ServiceCardType;
                if (!updatedServiceCard.sale.hasPendingOnlinePayment) {
                    clearInterval(intervalRef.current!)
                    if (updatedServiceCard.sale.remainingPrice.vatIncluded === 0) {
                        setServiceCard(updatedServiceCard)
                        setPrintReceipt(true)
                    } else {
                        setEditingEmail(true)
                        setPayOnlineResponse(null)
                        setPayOnlinePrice(null)
                        setPaymentNotice(t('selfService.onlinePaymentFailed'))
                    }
                } else {
                    setPayOnlinePrice(updatedServiceCard.sale?.remainingPrice?.vatIncluded ?? null)
                }
            }
        }

        console.log('Initiating payment with email:', email)
        if (email === "") {
            return;
        }
        const response: ApiResponse<any> = await createPayment({
            token: serviceCard.token,
            email: email
        });
        if (response.ok) {
            setPayOnlineResponse(response.data)
            await fetchUpdatedServiceCard()
        }

        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }

        intervalRef.current = setInterval(fetchUpdatedServiceCard, 5000)
    }

    const cancelCurrentPayment = async () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }

        const response: ApiResponse<any> = await cancelPayment({
            token: serviceCard.token,
        });

        if (response.ok) {
            setPayOnlineResponse(null)
        }
    }

    useEffect(() => {
        if (serviceCard?.token && email !== '') {
            initiatePayment();
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        };
    }, [serviceCard.token])

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    };

    const validateEmail = (email: string) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };

    const handleConfirmEmail = () => {
        if (email === '') {
            setError(t('errors.required'));
        } else if (!validateEmail(email)) {
            setError(t('errors.email'));
        } else {
            setError(null);
            setPaymentNotice(null)
            setPayOnlineResponse(null)
            initiatePayment();
            setEditingEmail(false);
        }
    };

    const handleEditEmail = () => {
        setEditingEmail(true)
        setPaymentNotice(t('selfService.onlinePaymentChangeEmailInfo'))
    };

    const onPrintDone = () => {
        setPaymentMade(true)
    };

    const handleBack = () => {
        cancelCurrentPayment().finally(onBack)
    }

    return (
        <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems="center" spacing={2} className="pageMainContainer">
            <Grid className="pageMainHeader">
                <PaymentHeaders serviceCard={serviceCard} />
            </Grid>
            <Grid width="75%" maxWidth="700px">
                {editingEmail === true && <Grid container wrap="nowrap" direction="column" spacing={1} >
                    <Grid>
                        <Typography variant="subtitle1">
                            {t('selfService.giveEmailForOnlinePayment')}
                        </Typography>
                    </Grid>
                    <Grid width="100%">
                        <Stack direction="row" spacing={2}>
                            <TextField
                                id="email"
                                placeholder={t('common.email')}
                                type={'email'}
                                value={email}
                                fullWidth
                                onChange={handleEmailChange}
                                onKeyUp={(e) => {
                                    if (e.key === 'Enter') {
                                        handleConfirmEmail()
                                    }
                                }}
                                error={!!error}
                                helperText={error}
                            />
                            <Button
                                variant="contained"
                                size="small"
                                color="secondary"
                                className="buttonCompact"
                                onClick={handleConfirmEmail}
                            >
                                {t('selfService.confirm')}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>}
            </Grid>
            {editingEmail === true && paymentNotice ?
                <Grid>
                    <Typography variant="body1" color="error">
                        {paymentNotice}
                    </Typography>
                </Grid>
                :
                null
            }
             <Grid>
                {!printReceipt && editingEmail === false && payOnlineResponse && <Typography variant="h5" gutterBottom>
                    {serviceCard.sale.tasks[0].basket.items[0].name} {payOnlinePrice ? numberToCurrencyString(payOnlinePrice, 'EUR', 0) : null}
                </Typography>}
            </Grid>
            <Grid>
                {!printReceipt && editingEmail === false && payOnlineResponse &&
                    <Typography>
                        <QRCodeSVG size={192} value={payOnlineResponse.href}/>
                    </Typography>
                }
                {!printReceipt && editingEmail === false && payOnlineResponse === null &&
                    <Typography>
                        <CircularProgress color="primary" size={48}/>
                    </Typography>
                }
            </Grid>
            <Grid style={{textAlign: 'center'}}>
                {!printReceipt && editingEmail === false && payOnlineResponse && <>
                    <Typography gutterBottom>
                        {t('selfService.scanQRCode')}
                    </Typography>
                    <Typography>
                        {t('selfService.emailForOnlinePayment')}: {email} (<Button size="small" className="textButtonCompact" variant="text" href="#" onClick={handleEditEmail}>{t('selfService.change')}</Button>)
                    </Typography>
                </>}
                {printReceipt && <PrintReceipt serviceCard={serviceCard} type="receipt" onPrintDone={onPrintDone} />}
            </Grid>
            <Grid className="footerButtons">
                <Grid container wrap="nowrap" spacing={4} justifyContent="space-between" alignItems="end">
                    <BackButton onBack={handleBack} />
                </Grid>
            </Grid>
        </Grid>
    )
}

export default PayOnline
